import React from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useSubscription } from "@/hooks/useSubscription";
import SupportBubble from "@/components/SupportBubble";
import { Zap, Sparkles, Repeat, Gauge, Library, LogOut, Crown, Gift, ShieldAlert, Wand2, Calendar as CalendarIcon } from "lucide-react";

const navItems = [
  { to: "/app", label: "Dashboard", icon: Zap, testid: "nav-dashboard" },
  { to: "/app/hooks", label: "Hook Forge", icon: Sparkles, testid: "nav-hooks" },
  { to: "/app/remix", label: "Remix Lab", icon: Repeat, testid: "nav-remix" },
  { to: "/app/score", label: "Score Engine", icon: Gauge, testid: "nav-score" },
  { to: "/app/library", label: "Vault", icon: Library, testid: "nav-library" },
  { to: "/app/calendar", label: "Calendar", icon: CalendarIcon, testid: "nav-calendar" },
  { to: "/app/brand-voice", label: "Voice", icon: Wand2, testid: "nav-voice" },
  { to: "/app/referrals", label: "Refer/Earn", icon: Gift, testid: "nav-referrals" },
];

const adminNav = { to: "/app/admin", label: "Admin", icon: ShieldAlert, testid: "nav-admin" };

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { status } = useSubscription();
  const navigate = useNavigate();
  const isPro = !!status?.is_pro;
  const remaining = status?.remaining;
  const items = user?.is_admin ? [...navItems, adminNav] : navItems;

  const onLogout = () => {
    logout();
    navigate("/auth");
  };

  return (
    <div className="min-h-screen bg-void text-white flex">
      {/* Sidebar */}
      <aside className="hidden md:flex w-64 shrink-0 flex-col border-r border-white/10 bg-[#0A0A0A]">
        <div className="p-6 border-b border-white/10">
          <Link to="/app" className="flex items-center gap-3" data-testid="brand-logo">
            <div className="w-9 h-9 bg-signal-yellow text-black flex items-center justify-center font-mono font-bold">V</div>
            <div>
              <div className="heading-display text-lg leading-none">VIRAL/CO</div>
              <div className="font-mono text-[10px] text-zinc-500 tracking-[0.2em] mt-1">CONTENT MACHINE</div>
            </div>
          </Link>
        </div>

        <nav className="flex-1 p-3 space-y-1">
          {items.map(({ to, label, icon: Icon, testid }) => (
            <NavLink
              key={to}
              to={to}
              end={to === "/app"}
              data-testid={testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 font-mono text-xs uppercase tracking-[0.18em] transition-colors ${
                  isActive
                    ? "bg-signal-yellow text-black"
                    : "text-zinc-400 hover:text-white hover:bg-white/5"
                }`
              }
            >
              <Icon size={16} />
              {label}
            </NavLink>
          ))}
        </nav>

        <div className="p-4 border-t border-white/10 space-y-3">
          {/* Plan badge */}
          <div
            data-testid="plan-badge"
            className={`flex items-center justify-between px-3 py-2 border ${
              isPro ? "border-signal-yellow bg-signal-yellow/10" : "border-white/15"
            }`}
          >
            <div className="flex items-center gap-2">
              <Crown size={12} className={isPro ? "text-signal-yellow" : "text-zinc-500"} />
              <span className={`font-mono text-[10px] tracking-[0.2em] uppercase ${isPro ? "text-signal-yellow" : "text-zinc-400"}`}>
                {isPro ? "PRO" : "FREE"}
              </span>
            </div>
            {!isPro && typeof remaining === "number" && (
              <span data-testid="quota-remaining" className="font-mono text-[10px] text-zinc-500">{remaining}/5 left</span>
            )}
          </div>
          {!isPro && (
            <Link
              to="/app/pricing"
              data-testid="upgrade-nav"
              className="w-full block text-center bg-signal-yellow text-black font-bold uppercase tracking-[0.18em] text-[11px] px-3 py-2 hover:bg-signal-yellow-hover transition-colors"
            >
              ⚡ Upgrade to Pro
            </Link>
          )}
          <div className="font-mono text-[10px] tracking-[0.2em] text-zinc-500 uppercase pt-1">Operator</div>
          <div className="font-display text-sm leading-tight" data-testid="user-name">{user?.name}</div>
          <div className="font-mono text-[10px] text-zinc-500 truncate" data-testid="user-email">{user?.email}</div>
          <button
            data-testid="logout-btn"
            onClick={onLogout}
            className="w-full mt-2 flex items-center justify-center gap-2 border border-white/15 hover:border-signal-red hover:text-signal-red px-3 py-2 font-mono text-xs uppercase tracking-[0.18em] transition-colors"
          >
            <LogOut size={14} /> Sign out
          </button>
          <a
            href="/support"
            data-testid="sidebar-support"
            className="block text-center font-mono text-[10px] text-zinc-500 hover:text-signal-yellow tracking-[0.18em] uppercase pt-2"
          >
            Need help? · Support
          </a>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 inset-x-0 z-40 bg-[#0A0A0A] border-b border-white/10 flex items-center justify-between px-4 py-3">
        <Link to="/app" className="flex items-center gap-2">
          <div className="w-7 h-7 bg-signal-yellow text-black flex items-center justify-center font-mono font-bold text-sm">V</div>
          <span className="heading-display text-sm">VIRAL/CO</span>
        </Link>
        <div className="flex items-center gap-2">
          {!isPro && (
            <Link
              to="/app/pricing"
              data-testid="upgrade-mobile-top"
              className="bg-signal-yellow text-black font-bold uppercase tracking-[0.18em] text-[10px] px-3 py-1.5 hover:bg-signal-yellow-hover transition-colors"
            >
              ⚡ Upgrade
            </Link>
          )}
          <button data-testid="logout-btn-mobile" onClick={onLogout} className="font-mono text-[10px] tracking-[0.2em] uppercase text-zinc-400">Exit</button>
        </div>
      </div>

      {/* Main */}
      <main className="flex-1 min-w-0 md:pt-0 pt-14">
        {children}
        <div className="md:hidden border-t border-white/10 grid grid-cols-6 sticky bottom-0 bg-[#0A0A0A]">          {navItems.map(({ to, label, icon: Icon, testid }) => (
            <NavLink
              key={to}
              to={to}
              end={to === "/app"}
              data-testid={`${testid}-mobile`}
              className={({ isActive }) =>
                `flex flex-col items-center gap-1 py-3 font-mono text-[9px] uppercase tracking-wider ${
                  isActive ? "text-signal-yellow" : "text-zinc-500"
                }`
              }
            >
              <Icon size={16} />
              {label}
            </NavLink>
          ))}
        </div>
      </main>
      <SupportBubble />
    </div>
  );
}
